'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { useId, useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ContactTabsEshop } from './tabs/contact-tabs-eshop';
import { ContactTabsCompany } from './tabs/contact-tabs-company';
import { ContactTabsBilling } from './tabs/contact-tabs-billing';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export function ContactTabs({
  opening1,
  opening2,
  openingCompany1,
  openingCompany2,
}: {
  opening1: string;
  opening2: string;
  openingCompany1: string;
  openingCompany2: string;
}) {
  const [activeTab, setActiveTab] = useState<string>('1');
  const tabsId = useId();
  const panelId = `${tabsId}-panel`;

  const { t } = useTranslations();

  const tabs = [
    {
      id: '1',
      name: t('text298'),
      content: <ContactTabsEshop opening1={opening1} opening2={opening2} />,
    },
    {
      id: '2',
      name: t('text118'),
      content: <ContactTabsCompany opening1={openingCompany1} opening2={openingCompany2} />,
    },
    {
      id: '3',
      name: t('text679'),
      content: <ContactTabsBilling />,
    },
  ];

  return (
    <Tabs
      className="flex w-full flex-col items-start justify-start gap-12"
      value={activeTab}
      onValueChange={setActiveTab}
      defaultValue={activeTab}
    >
      <TabsList className="flex h-fit w-full items-center justify-start gap-2 overflow-x-auto rounded-none bg-transparent p-0 shadow-none sm:gap-4 lg:gap-6">
        {tabs.map(tab => (
          <TabsTrigger
            id={`${tabsId}-trigger-${tab.id}`}
            aria-controls={panelId}
            value={tab.id}
            key={tab.id}
            className={cn(
              'flex h-10 items-center justify-center whitespace-nowrap rounded-[50px] px-6 text-sm transition-all sm:h-11 sm:text-base lg:h-[50px]',
              'ea-universal-tabs__tab-trigger',
            )}
          >
            {tab.name}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent id={panelId} aria-labelledby={`${tabsId}-trigger-${activeTab}`} value={activeTab} className="w-full pr-6">
        {tabs.find(tab => tab.id === activeTab)?.content}
      </TabsContent>
    </Tabs>
  );
}

export function ContactTabIcon({ imageUrl }: { imageUrl: string; alt: string }) {
  return (
    <div className="flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center overflow-hidden rounded-[12px] bg-black/5 sm:max-h-14 sm:min-h-14 sm:min-w-14 sm:max-w-14 lg:max-h-16 lg:min-h-16 lg:min-w-16 lg:max-w-16">
      <Image
        src={imageUrl}
        alt=""
        width={0}
        height={0}
        className="h-6 w-6 object-contain object-center sm:h-8 sm:w-8"
      />
    </div>
  );
}

export function ContactTabTitle({ title }: { title: string }) {
  return <h2 data-ea-visual-tag="h5" className="text-xl font-medium sm:text-2xl">{title}</h2>;
}

export function ContactTabContainer({ children }: { children: React.ReactNode }) {
  return <div className="flex w-full flex-col items-start justify-start gap-6">{children}</div>;
}
